import { GoAFormItem, GoAFileUploadInput } from "@abgov/react-components";

import MediaUploadCard from "./media-upload-card";

import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import axios, { AxiosInstance } from "axios";
import useCsrService from "../services/csr-api-redux";

interface Uploader {
  upload: (url: string | ArrayBuffer) => void;
  abort: () => void;
}

interface Upload {
  file: File;
  uploader: Uploader;
  isUploaded: boolean;
}

interface MediaProps {}

class FileUploader {
  private progress: number = 0;
  private intervalSteps = 10;
  private totalIntervals = 100; // Number of intervals to reach 100%
  private timer: number | null = null;
  private currentStep = 0;
  private totalSteps = 0;
  private csrService: AxiosInstance;

  constructor(csrService: AxiosInstance) {
    this.csrService = csrService;
  }

  onabort: (() => void) | null = null;
  onfail: ((error: Error) => void) | null = null;
  oncomplete: (() => void) | null = null;
  onprogress: ((percent: number) => void) | null = null;

  upload(collisionId: string, files: File[]) {
    this.totalSteps = files.length;
    this.currentStep = 0;
    this.timer = window.setInterval(() => {
      this.currentStep++;
      if (this.currentStep >= this.totalSteps) {
        this.progressUpdate(100);
        this.complete();
      } else {
        const percent = (this.currentStep / this.totalSteps) * 100;
        this.progressUpdate(percent);
      }
    }, 100);

    // Iterate over files and upload each one
    files.forEach((file) => {
      const formData = new FormData();
      formData.append("files", file, file.name);

      this.csrService
        .post(`/api/v1/collisions/${collisionId}/media/image`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(() => {
          this.batchItemUploaded();
        })
        .catch((error) => {
          if (this.onfail) this.onfail(error);
        });
    });
  }

  abort() {
    // Simulate aborting the upload
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
      this.abortUpload();
    }
  }

  private progressUpdate(percent: number) {
    if (this.onprogress) {
      this.onprogress(Math.round(percent)); // Round to the nearest integer
    }
  }

  private complete() {
    if (this.oncomplete) {
      this.oncomplete();
    }
    clearInterval(this.timer!); // Use the non-null assertion operator to suppress the error
    this.timer = null;
  }

  private abortUpload() {
    if (this.onabort) {
      this.onabort();
    }
  }

  private batchItemUploaded() {
    this.currentStep++;
    const percent = (this.currentStep / this.totalSteps) * 100;
    this.progressUpdate(percent);

    if (this.currentStep >= this.totalSteps) {
      this.complete();
    }
  }
}

const MediaComponent = ({}: MediaProps) => {
  let navigate = useNavigate();

  const csrService = useCsrService();

  // Define state for collisionId
  const [collisionId, setCollisionId] = useState<string>("");
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [progressList, setProgressList] = useState<Record<string, number>>({});

  // Set a mock value for collisionId
  useEffect(() => {
    setCollisionId("PFM4S");
  }, []);

  function deleteFile(upload: Upload) {
    csrService
      .delete(
        `/api/v1/collisions/${collisionId}/media/image/${upload.file.name}`
      )
      .then(() => {
        setUploads((uploads) => {
          return uploads.filter((u) => u.file.name !== upload.file.name);
        });
        console.log("File deleted successfully");
      })
      .catch((error) => {
        console.error("Failed to delete file:", error);
      });
  }

  function uploadFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (!e.target || !e.target.result) return;
      if (uploads.find((upload: any) => upload.file.name === file.name)) {
        return;
      }

      // let url = e.target.result;

      const uploader = new FileUploader(csrService);
      setUploads((old: any) => [...old, { file, uploader, isUploaded: false }]);
      //setUploads((old: any) => [...old, { file, uploader }]);

      uploader.onabort = () => console.log("Aborting upload");
      uploader.onfail = (err) => console.log("Upload failed: ", err);
      //uploader.oncomplete = () => console.log("File upload complete - ", file.size);
      uploader.oncomplete = () => {
        console.log("File upload complete");
        setUploads((old) =>
          old.map((u) =>
            u.file.name === file.name ? { ...u, isUploaded: true } : u
          )
        );
      };
      uploader.onprogress = (percent) => {
        setProgressList((old) => ({ ...old, [file.name]: percent }));
      };

      uploader.upload(collisionId, [file]);
    };
    reader.readAsDataURL(file);
  }

  return (
    <div>
      <GoAFormItem label="Upload a file">
        <GoAFileUploadInput
          onSelectFile={uploadFile}
          variant="dragdrop"
          maxFileSize="500MB"
        />
        {uploads.map(
          (upload) =>
            upload.isUploaded && (
              <MediaUploadCard
                key={upload.file.name}
                collisionId={collisionId}
                filename={upload.file.name}
                size={upload.file.size}
                onRemove={() => deleteFile(upload)}
              />
            )
        )}
      </GoAFormItem>
    </div>
  );
};

export default MediaComponent;
